"use client";

import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import { Button } from "@/app/(public)/shared/ui/Button";
import "@/app/(public)/shared/tokens/animations.css";
import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import "./security.css";
import { ReactNode } from "react";

const TRUST_CENTER_URL = "https://trust.nowadays.ai";
const STATUS_URL = "https://status.nowadays.ai";
const SECURITY_EMAIL = "security@nowadays.ai";
const SUPPORT_EMAIL = "support@nowadays.ai";
const LAST_UPDATED = "July 2026";

type Capability = { label: string; description: string };
type Section = { title: string; intro: string; items: Capability[]; cta?: { href: string; label: string } };

const SECTIONS: Section[] = [
  {
    title: "Compliance",
    intro: "A formal security program stands behind everything below, with controls monitored continuously.",
    items: [
      { label: "SOC 2 Type II", description: "In progress, with controls monitored continuously." },
      { label: "GDPR and CCPA", description: "Aligned for data privacy and user rights." },
      {
        label: "Independent penetration test",
        description: "Run by a third-party firm in 2026, with findings tracked to closure.",
      },
      {
        label: "Security policies",
        description: "18 policies covering access, cryptography, risk, and vendors, reviewed annually.",
      },
      {
        label: "Vendor risk",
        description: "We require data processing agreements with subprocessors that handle customer data.",
      },
    ],
    cta: { href: TRUST_CENTER_URL, label: "View our subprocessors and security documentation in the Trust Center" },
  },
  {
    title: "Data protection",
    intro: "Your data is encrypted, backed up, and yours to control.",
    items: [
      { label: "Encryption in transit", description: "TLS 1.2+, with HTTPS enforced site-wide through HSTS." },
      { label: "Encryption at rest", description: "AES-256 for all data stored in our database." },
      { label: "Backups", description: "Automated, with documented recovery procedures." },
      { label: "Ownership", description: "Export or delete your data on request." },
      { label: "Minimization", description: "We collect only what running your events requires." },
    ],
  },
  {
    title: "AI and data governance",
    intro: "We are an AI product, so we govern how models use your data as carefully as the data itself.",
    items: [
      {
        label: "No model training",
        description: "We require AI providers not to train their models on your data.",
      },
      {
        label: "Provider agreements",
        description: "We require data processing agreements with the AI providers we use.",
      },
      { label: "Scoped access", description: "Model access to your data is limited to the features that need it." },
      { label: "AI policies", description: "Dedicated AI governance, responsible-AI, and AI privacy policies." },
    ],
  },
  {
    title: "Access and application security",
    intro: "Every request is authenticated, and security is built into how we ship code.",
    items: [
      {
        label: "Authentication",
        description: "Industry-standard password hashing through a managed authentication provider.",
      },
      { label: "Sessions", description: "Email verification for new accounts and automatic session expiration." },
      { label: "API security", description: "Bearer-token validation on every non-public route." },
      {
        label: "Access control",
        description: "Role-based access, with tenant data isolated at the database level.",
      },
      {
        label: "Injection defense",
        description:
          "Parameterized queries and input sanitization guard against SQL injection and cross-site scripting.",
      },
      {
        label: "Secure development",
        description: "Code review and automated tests in CI before changes ship.",
      },
    ],
  },
  {
    title: "Infrastructure and availability",
    intro: "We run on audited cloud platforms with defenses at the network edge.",
    items: [
      { label: "Cloud platforms", description: "Independently audited cloud hosting and database providers." },
      { label: "Network", description: "Managed DDoS protection and network isolation at the edge." },
      { label: "Monitoring", description: "Access and activity logging for investigation." },
      { label: "Continuity", description: "Documented business continuity and disaster recovery plans." },
    ],
  },
];

/**
 * Public security page rendered at /security.
 *
 * @returns A restrained overview of Nowadays AI's security posture: a centered
 *   hero over the shared aurora background, then hairline-divided control
 *   sections presented as labeled capabilities, plus incident-response and
 *   contact details.
 */
export default function Security() {
  // Render
  return (
    <>
      {/* Full-bleed aurora background, matching the /team page. It is absolutely
          positioned against the (legal) layout root (which is `relative` for
          exactly this reason), so it spans the full page width rather than the
          `container-page` content width this page is nested inside. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${backgroundOverlay.src})`,
          backgroundSize: "100% auto",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          opacity: 0.3,
        }}
      />

      <div className="container-top-form relative z-[1] w-full">
        <div className="mx-auto w-full max-w-5xl">
          <Hero />

          <div className="security-section-list mt-16 flex flex-col gap-14 md:mt-24 md:gap-20">
            {SECTIONS.map((section) => (
              <SectionShell key={section.title} title={section.title} intro={section.intro}>
                <CapabilityGrid items={section.items} />
                {section.cta ? (
                  <a
                    href={section.cta.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="security-link mt-8 inline-block text-body"
                  >
                    {section.cta.label} ↗
                  </a>
                ) : null}
              </SectionShell>
            ))}
            <IncidentResponse />
            <Contact />
          </div>
        </div>
      </div>
    </>
  );
}

// Hero

function Hero() {
  return (
    <section className="flex flex-col items-center gap-6 text-center">
      <h1 className="animate-rise-in text-h1 text-landing-primary" style={{ textWrap: "balance" }}>
        Security you can trust
      </h1>

      <p className="animate-rise-in max-w-2xl text-body-lg text-landing-secondary" style={{ animationDelay: "80ms" }}>
        Nowadays protects the events, contacts, and communications you share with us. Here is how we secure that data,
        and where you can review our controls and documentation for yourself.
      </p>

      <div
        className="animate-rise-in flex flex-col items-center gap-2 sm:flex-row sm:justify-center"
        style={{ animationDelay: "160ms" }}
      >
        <Button
          variant="primary"
          color="primary"
          size="lg"
          arrow
          onClick={() => window.open(TRUST_CENTER_URL, "_blank", "noopener,noreferrer")}
          className="w-full sm:w-auto"
        >
          Open Trust Center
        </Button>
        <Button
          variant="outline"
          color="neutral"
          size="lg"
          onClick={() => (window.location.href = `mailto:${SECURITY_EMAIL}`)}
          className="w-full sm:w-auto"
        >
          Contact security
        </Button>
      </div>
    </section>
  );
}

// Section primitives

function SectionShell({ title, intro, children }: { title: string; intro?: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="text-h3 text-landing-primary" style={{ textWrap: "balance" }}>
        {title}
      </h2>
      {intro ? (
        <p className="mt-3 max-w-2xl text-body-lg text-landing-secondary" style={{ textWrap: "pretty" }}>
          {intro}
        </p>
      ) : null}
      {children}
    </section>
  );
}

function CapabilityGrid({ items }: { items: Capability[] }) {
  return (
    <div className="mt-10 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-1.5">
          <h3 className="text-tag-md text-landing-primary">{item.label}</h3>
          <p className="text-body text-landing-secondary" style={{ textWrap: "pretty" }}>
            {item.description}
          </p>
        </div>
      ))}
    </div>
  );
}

// Incident response

function IncidentResponse() {
  return (
    <SectionShell
      title="Incident response"
      intro="We maintain an incident response plan and welcome reports from security researchers."
    >
      <p className="mt-4 max-w-2xl text-body text-landing-secondary" style={{ textWrap: "pretty" }}>
        If a security incident affects your data, we will notify you promptly with clear information about what happened
        and how we responded. Follow live system status at{" "}
        <a href={STATUS_URL} target="_blank" rel="noopener noreferrer" className="security-link">
          status.nowadays.ai
        </a>
        , and report a suspected vulnerability to{" "}
        <a href={`mailto:${SECURITY_EMAIL}`} className="security-link">
          {SECURITY_EMAIL}
        </a>
        .
      </p>
    </SectionShell>
  );
}

// Contact

function Contact() {
  return (
    <SectionShell title="Contact">
      <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
        <ContactItem label="General questions" value={SUPPORT_EMAIL} href={`mailto:${SUPPORT_EMAIL}`} />
        <ContactItem label="Security and vulnerabilities" value={SECURITY_EMAIL} href={`mailto:${SECURITY_EMAIL}`} />
        <ContactItem label="Compliance and documents" value="trust.nowadays.ai" href={TRUST_CENTER_URL} external />
        <ContactItem label="System status" value="status.nowadays.ai" href={STATUS_URL} external />
      </div>
      <p className="mt-12 text-body-sm text-landing-secondary">Last updated: {LAST_UPDATED}</p>
    </SectionShell>
  );
}

function ContactItem({
  label,
  value,
  href,
  external,
}: {
  label: string;
  value: string;
  href: string;
  external?: boolean;
}) {
  const externalProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-tag-md text-landing-primary">{label}</span>
      <a href={href} {...externalProps} className="security-link text-body">
        {value}
        {external ? " ↗" : null}
      </a>
    </div>
  );
}
